/** Minimal Vec3 / Quat math on plain `{x,y,z}` / `{x,y,z,w}` structs (no Three.js). */
import type { Vec3 } from '../types';

export type Quat = { x: number; y: number; z: number; w: number };

export const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });
export const cloneV = (a: Vec3): Vec3 => ({ x: a.x, y: a.y, z: a.z });

export const add = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
export const sub = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
export const scale = (a: Vec3, s: number): Vec3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
export const addScaled = (a: Vec3, b: Vec3, s: number): Vec3 => ({ x: a.x + b.x * s, y: a.y + b.y * s, z: a.z + b.z * s });
export const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;

export const cross = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});

export const lenSq = (a: Vec3): number => a.x * a.x + a.y * a.y + a.z * a.z;
export const len = (a: Vec3): number => Math.sqrt(lenSq(a));

export function normalize(a: Vec3): Vec3 {
  const l = len(a);
  return l > 1e-12 ? scale(a, 1 / l) : v3(0, 0, 0);
}

/** Horizontal (xy) length — most carom motion is planar. */
export const lenXY = (a: Vec3): number => Math.hypot(a.x, a.y);

// ---- quaternions (for visualising ball spin) ----

export const quatIdentity = (): Quat => ({ x: 0, y: 0, z: 0, w: 1 });

export function quatMul(a: Quat, b: Quat): Quat {
  return {
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
  };
}

export function quatNormalize(q: Quat): Quat {
  const l = Math.hypot(q.x, q.y, q.z, q.w) || 1;
  return { x: q.x / l, y: q.y / l, z: q.z / l, w: q.w / l };
}

/** Advance orientation `q` by angular velocity `w` (rad/s) over `dt`. */
export function integrateOrientation(q: Quat, w: Vec3, dt: number): Quat {
  const wq: Quat = { x: w.x, y: w.y, z: w.z, w: 0 };
  const dq = quatMul(wq, q);
  return quatNormalize({
    x: q.x + 0.5 * dq.x * dt,
    y: q.y + 0.5 * dq.y * dt,
    z: q.z + 0.5 * dq.z * dt,
    w: q.w + 0.5 * dq.w * dt,
  });
}

export function quatSlerp(a: Quat, b: Quat, t: number): Quat {
  let cosHalf = a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
  let bx = b.x, by = b.y, bz = b.z, bw = b.w;
  if (cosHalf < 0) {
    cosHalf = -cosHalf;
    bx = -bx; by = -by; bz = -bz; bw = -bw;
  }
  if (cosHalf > 0.9995) {
    return quatNormalize({
      x: a.x + (bx - a.x) * t,
      y: a.y + (by - a.y) * t,
      z: a.z + (bz - a.z) * t,
      w: a.w + (bw - a.w) * t,
    });
  }
  const half = Math.acos(cosHalf);
  const sinHalf = Math.sin(half);
  const ra = Math.sin((1 - t) * half) / sinHalf;
  const rb = Math.sin(t * half) / sinHalf;
  return { x: a.x * ra + bx * rb, y: a.y * ra + by * rb, z: a.z * ra + bz * rb, w: a.w * ra + bw * rb };
}
